'use client';

import type { FC, PropsWithChildren } from 'react';
import { createContext, memo, useContext, useState } from 'react';

import {
  useAuiState,
  TextMessagePartProvider,
  type ReasoningMessagePartComponent,
  type ReasoningGroupComponent,
} from '@assistant-ui/react';
import { BrainIcon } from 'lucide-react';

import { MarkdownText } from '@/components/assistant-ui/markdown-text';
import { ChevronToggle } from '@/components/ui/chevron-toggle';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

const ReasoningConfigContext = createContext<{ groupAll: boolean }>({ groupAll: false });

interface ReasoningContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const ReasoningContext = createContext<ReasoningContextValue | null>(null);

const ReasoningRoot: FC<PropsWithChildren<{ className?: string }>> = ({ className, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ReasoningContext.Provider value={{ isOpen, setIsOpen }}>
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        dir="ltr"
        className={cn('aui-reasoning-root mb-4 w-full', className)}
      >
        {children}
      </Collapsible>
    </ReasoningContext.Provider>
  );
};

ReasoningRoot.displayName = 'ReasoningRoot';

const ReasoningTrigger: FC<{ isStreaming: boolean; className?: string }> = ({
  isStreaming,
  className,
}) => {
  const context = useContext(ReasoningContext);
  if (!context) throw new Error('ReasoningTrigger must be used within ReasoningRoot');

  const { isOpen } = context;

  return (
    <CollapsibleTrigger
      className={cn(
        'aui-reasoning-trigger -mb-2 flex max-w-[75%] items-center gap-2 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground',
        className,
      )}
    >
      <BrainIcon className="aui-reasoning-trigger-icon size-4 shrink-0" />
      <span className="aui-reasoning-trigger-label-wrapper relative inline-block leading-none">
        <span>Reasoning</span>
        {isStreaming && (
          <span
            aria-hidden
            className="aui-reasoning-trigger-shimmer pointer-events-none absolute inset-0 shimmer motion-reduce:animate-none"
          >
            Reasoning
          </span>
        )}
      </span>
      <ChevronToggle isOpen={isOpen} className="aui-reasoning-trigger-chevron mt-0.5" />
    </CollapsibleTrigger>
  );
};

ReasoningTrigger.displayName = 'ReasoningTrigger';

const ReasoningContent: FC<PropsWithChildren<{ className?: string; isStreaming?: boolean }>> = ({
  className,
  children,
  isStreaming,
}) => (
  <CollapsibleContent
    className={cn('aui-reasoning-content animate-none! text-sm text-muted-foreground', className)}
    aria-busy={isStreaming}
  >
    {children}
  </CollapsibleContent>
);

ReasoningContent.displayName = 'ReasoningContent';

const ReasoningText: FC<PropsWithChildren<{ className?: string }>> = ({ className, children }) => (
  <div
    className={cn('aui-reasoning-text space-y-4 ps-6 pt-4 leading-relaxed [&_p]:-mb-2', className)}
  >
    {children}
  </div>
);

ReasoningText.displayName = 'ReasoningText';

const AllReasoningContent: FC = () => {
  const allReasoningText = useAuiState(({ message }) => {
    const reasoningParts = message.parts.filter(part => part.type === 'reasoning');
    return reasoningParts.map(part => ('text' in part ? part.text : '')).join('\n\n');
  });

  if (!allReasoningText) return null;

  return (
    <TextMessagePartProvider text={allReasoningText} isRunning={false}>
      <MarkdownText />
    </TextMessagePartProvider>
  );
};

const ReasoningImpl: ReasoningMessagePartComponent = () => {
  return <MarkdownText />;
};

const ReasoningGroupImpl: ReasoningGroupComponent = ({ children, startIndex }) => {
  const { groupAll } = useContext(ReasoningConfigContext);

  const isFirstGroup = useAuiState(({ message }) => {
    const firstReasoningIndex = message.parts.findIndex(part => part.type === 'reasoning');
    return firstReasoningIndex !== -1 && startIndex === firstReasoningIndex;
  });

  const isReasoningStreaming = useAuiState(({ message }) => {
    if (message.status?.type !== 'running') return false;
    const lastIndex = message.parts.length - 1;
    return lastIndex >= 0 && message.parts[lastIndex]?.type === 'reasoning';
  });

  if (groupAll) {
    if (!isFirstGroup) return null;

    return (
      <ReasoningRoot>
        <ReasoningTrigger isStreaming={isReasoningStreaming} />
        <ReasoningContent isStreaming={isReasoningStreaming}>
          <ReasoningText>
            <AllReasoningContent />
          </ReasoningText>
        </ReasoningContent>
      </ReasoningRoot>
    );
  }

  return (
    <ReasoningRoot>
      <ReasoningTrigger isStreaming={isReasoningStreaming} />
      <ReasoningContent isStreaming={isReasoningStreaming}>
        <ReasoningText>{children}</ReasoningText>
      </ReasoningContent>
    </ReasoningRoot>
  );
};

export const Reasoning = memo(ReasoningImpl);
Reasoning.displayName = 'Reasoning';

export const ReasoningGroup = memo(ReasoningGroupImpl);
ReasoningGroup.displayName = 'ReasoningGroup';

export const ReasoningConfigProvider: FC<PropsWithChildren<{ groupAll: boolean }>> = ({
  groupAll,
  children,
}) => (
  <ReasoningConfigContext.Provider value={{ groupAll }}>{children}</ReasoningConfigContext.Provider>
);
